import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Crown, Check, Copy, Smartphone, QrCode, ShieldCheck, Sparkles, Download, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { usePlans } from '../hooks/usePlans';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { API } from '../context/AuthContext';

const UPI_ID = '9078325300@ubl';
const PAYEE_NAME = 'Gurusevakendra';

// Detect if user is on mobile device
const isMobile = () => /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(
  typeof navigator !== 'undefined' ? navigator.userAgent : ''
);

export default function PaymentPage() {
  const { user, login } = useAuth();
  const [selected, setSelected] = useState('quarterly');
  const [txnId, setTxnId] = useState('');
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const mobile = isMobile();
  const { plans: premiumPlans } = usePlans();

  const plan = premiumPlans.find((p) => p.id === selected);
  const amount = plan.price;

  const upiParams = `pa=${encodeURIComponent(UPI_ID)}&pn=${encodeURIComponent(PAYEE_NAME)}&am=${amount}&cu=INR&tn=${encodeURIComponent('Premium ' + plan.name)}`;
  const upiLink = `upi://pay?${upiParams}`;
  const gpayLink = `tez://upi/pay?${upiParams}`;
  const phonepeLink = `phonepe://pay?${upiParams}`;
  const paytmLink = `paytmmp://pay?${upiParams}`;
  const bhimLink = `bhim://pay?${upiParams}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(upiLink)}&margin=10`;

  const copyUpi = () => {
    try {
      navigator.clipboard.writeText(UPI_ID);
      toast({ title: 'UPI ID କପି ହୋଇଛି ✅', description: UPI_ID });
    } catch (e) {
      toast({ title: 'Copy ହୋଇପାରିଲା ନାହିଁ', description: 'ଦୟାକରି ମାନୁଆଲି ଲେଖନ୍ତୁ: ' + UPI_ID });
    }
  };

  const openApp = (link, appName) => {
    if (!mobile) {
      toast({
        title: 'ମୋବାଇଲ୍ ଆବଶ୍ୟକ 📱',
        description: 'UPI App ମୋବାଇଲ୍‌ରେ ହିଁ କାମ କରେ। ଦୟାକରି ଉପରର QR କୋଡ୍ ସ୍କାନ କରନ୍ତୁ କିମ୍ବା UPI ID Copy କରି ପେମେଣ୍ଟ କରନ୍ତୁ।'
      });
      return;
    }
    // Try to open app; if it fails after 1.5s, show fallback message
    const start = Date.now();
    const timer = setTimeout(() => {
      if (Date.now() - start < 2000 && document.visibilityState === 'visible') {
        toast({
          title: `${appName} install ନାହିଁ ❌`,
          description: 'ଦୟାକରି QR କୋଡ୍ ସ୍କାନ କରନ୍ତୁ କିମ୍ବା UPI ID Copy କରି ଅନ୍ୟ UPI App ରୁ ପେମେଣ୍ଟ କରନ୍ତୁ।'
        });
      }
    }, 1500);
    try {
      window.location.href = link;
    } catch (e) {
      clearTimeout(timer);
      toast({
        title: `${appName} ଖୋଲିଲା ନାହିଁ ❌`,
        description: 'ଦୟାକରି QR କୋଡ୍ ସ୍କାନ କରନ୍ତୁ କିମ୍ବା UPI ID: ' + UPI_ID + ' କୁ Copy କରି ପେମେଣ୍ଟ କରନ୍ତୁ।'
      });
    }
  };

  const downloadQr = async () => {
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gurusevakendra-upi-${amount}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: 'QR download ହୋଇଛି ✅', description: 'ଆପଣଙ୍କ UPI App ରୁ Gallery ରୁ QR ସ୍କାନ କରନ୍ତୁ।' });
    } catch (e) {
      // Fallback: open in new tab if fetch fails (rare)
      window.open(qrUrl, '_blank');
      toast({ title: 'QR ନୂଆ tab ରେ ଖୋଲିଲା', description: 'ଛବି ଉପରେ long-press କରି save କରନ୍ତୁ।' });
    }
  };

  const submit = async () => {
    if (!user) { login(); return; }
    if (!txnId || txnId.length < 6) {
      toast({ title: 'Transaction ID ଆବଶ୍ୟକ', description: 'ପ୍ରାୟ ୧୨ digit UTR/UPI Ref number ଦିଅନ୍ତୁ' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/premium/request`, { plan: selected, transaction_id: txnId }, { withCredentials: true });
      toast({ title: 'ପେମେଣ୍ଟ ଯାଞ୍ଚ ପାଇଁ ପଠାଗଲା ✅', description: res.data.message });
      setStep(3);
    } catch (e) {
      toast({ title: 'ତ୍ରୁଟି', description: e?.response?.data?.detail || 'ପେମେଣ୍ଟ ଆବେଦନ ପଠାଇ ପାରିଲୁ ନାହିଁ' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="px-4 pt-4 space-y-5">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg"
           style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 60%, #ef4444 100%)' }}>
        <div className="absolute -top-8 -right-8 opacity-20"><Crown size={110} /></div>
        <Crown size={26} />
        <h1 className="text-xl font-extrabold mt-2">Premium ପ୍ରବେଶ କିଣନ୍ତୁ</h1>
        <p className="text-xs text-white/90 mt-1">UPI ମାଧ୍ୟମରେ ପେମେଣ୍ଟ କରନ୍ତୁ · Manual verification</p>
      </div>

      {step === 1 && (
        <>
          <div className="space-y-3">
            {premiumPlans.map((p) => {
              const active = selected === p.id;
              return (
                <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`plan-${p.id}`}
                  className={`w-full text-left rounded-2xl p-4 border-2 transition-all ${active ? 'border-emerald-500 bg-emerald-50/60 shadow-sm' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-base text-gray-900">{p.name}</span>
                        {p.popular && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white flex items-center gap-1"><Sparkles size={9} /> POPULAR</span>}
                      </div>
                      <div className="mt-1">
                        <span className="text-2xl font-extrabold text-gray-900" data-testid={`plan-${p.id}-price`}>₹{p.price}</span>
                        <span className="text-xs text-gray-500">{p.period}</span>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? 'border-emerald-600 bg-emerald-600' : 'border-gray-300'}`}>
                      {active && <Check size={12} className="text-white" strokeWidth={3} />}
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-gray-700">
                        <Check size={13} className="text-emerald-500 shrink-0" strokeWidth={3} /> {f}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
          <Button onClick={() => setStep(2)} data-testid="continue-to-pay-btn" className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base">
            ₹{amount} ପେମେଣ୍ଟ କରନ୍ତୁ →
          </Button>
        </>
      )}

      {step === 2 && (
        <>
          <Card className="rounded-2xl p-5 border-gray-200 text-center">
            <div className="text-sm font-semibold text-gray-800">📱 UPI QR ସ୍କାନ କରନ୍ତୁ · ₹{amount}</div>
            <div className="mt-3 inline-block p-3 bg-white border-2 border-emerald-200 rounded-2xl">
              <img src={qrUrl} alt="UPI QR" className="w-56 h-56" data-testid="upi-qr-image" />
            </div>
            <div className="text-[11px] text-gray-500 mt-2">GPay · PhonePe · Paytm · BHIM ଆଦିରେ ସ୍କାନ କରନ୍ତୁ</div>
            <button onClick={downloadQr} data-testid="download-qr-btn"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 border border-emerald-300 bg-emerald-50 px-3 py-1.5 rounded-full">
              <Download size={12} /> QR Download କରନ୍ତୁ
            </button>
          </Card>

          <Card className="rounded-2xl p-4 border-gray-200">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800"><Smartphone size={14} /> କିମ୍ବା UPI ID Copy କରନ୍ତୁ</div>
            <div className="mt-2 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <QrCode size={18} className="text-emerald-600" />
              <span className="font-mono font-bold text-emerald-800 flex-1 text-sm break-all" data-testid="upi-id-text">{UPI_ID}</span>
              <button onClick={copyUpi} data-testid="copy-upi-btn"
                className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg flex items-center gap-1">
                <Copy size={12} /> Copy
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">UPI ID Copy କରି ଆପଣଙ୍କ ପସନ୍ଦର UPI App (GPay, PhonePe, Paytm) ରେ paste କରି ₹{amount} ପେମେଣ୍ଟ କରନ୍ତୁ।</p>
          </Card>

          {mobile && (
            <Card className="rounded-2xl p-4 border-gray-200">
              <div className="text-sm font-semibold text-gray-800 mb-2">🚀 ସିଧାସଳଖ UPI App ଖୋଲନ୍ତୁ</div>
              <p className="text-[11px] text-gray-500 mb-3">ଆପଣଙ୍କ ଫୋନରେ install ଥିବା App ଉପରେ tap କରନ୍ତୁ:</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => openApp(gpayLink, 'Google Pay')} data-testid="pay-gpay-btn"
                  className="flex items-center justify-center gap-2 bg-white border-2 border-blue-200 hover:border-blue-400 text-blue-700 font-semibold py-2.5 rounded-xl text-xs">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-[10px] font-black">G</span> Google Pay
                </button>
                <button onClick={() => openApp(phonepeLink, 'PhonePe')} data-testid="pay-phonepe-btn"
                  className="flex items-center justify-center gap-2 bg-white border-2 border-purple-200 hover:border-purple-400 text-purple-700 font-semibold py-2.5 rounded-xl text-xs">
                  <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-[10px] font-black">Pe</span> PhonePe
                </button>
                <button onClick={() => openApp(paytmLink, 'Paytm')} data-testid="pay-paytm-btn"
                  className="flex items-center justify-center gap-2 bg-white border-2 border-sky-200 hover:border-sky-400 text-sky-700 font-semibold py-2.5 rounded-xl text-xs">
                  <span className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center text-[10px] font-black">P</span> Paytm
                </button>
                <button onClick={() => openApp(bhimLink, 'BHIM')} data-testid="pay-bhim-btn"
                  className="flex items-center justify-center gap-2 bg-white border-2 border-orange-200 hover:border-orange-400 text-orange-700 font-semibold py-2.5 rounded-xl text-xs">
                  <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-[10px] font-black">B</span> BHIM
                </button>
              </div>
              <button onClick={() => openApp(upiLink, 'UPI App')} data-testid="pay-generic-upi-btn"
                className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-xl text-sm">
                📱 ଅନ୍ୟ UPI App ରେ ଖୋଲନ୍ତୁ
              </button>
              <div className="mt-3 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle size={13} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-[10px] text-amber-800 leading-relaxed">ଯଦି App ଖୋଲି ନ ପାରୁଛି କିମ୍ବା "Not registered" error ଆସୁଛି — ଉପରର QR ସ୍କାନ କରନ୍ତୁ କିମ୍ବା UPI ID Copy କରନ୍ତୁ।</p>
              </div>
            </Card>
          )}

          <Card className="rounded-2xl p-4 border-gray-200">
            <div className="text-sm font-semibold text-gray-800">Transaction / UTR Number ଦିଅନ୍ତୁ</div>
            <p className="text-[11px] text-gray-500 mt-1">ପେମେଣ୍ଟ ପରେ ଆପଣଙ୍କ UPI app ରୁ 12-digit UTR/Reference number ଏଠାରେ ଲେଖନ୍ତୁ</p>
            <Input value={txnId} onChange={(e) => setTxnId(e.target.value)} placeholder="ଉଦା: 4589XXXX1234" className="mt-2 h-11 rounded-xl" data-testid="txn-id-input" />
            <Button onClick={submit} disabled={submitting} data-testid="submit-payment-btn" className="mt-3 w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {submitting ? 'ପଠାଉଛି...' : 'ଯାଞ୍ଚ ପାଇଁ ପଠାନ୍ତୁ'}
            </Button>
            <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-1"><ShieldCheck size={11} /> ୨୪ ଘଣ୍ଟା ମଧ୍ୟରେ premium activate ହେବ</p>
          </Card>
        </>
      )}

      {step === 3 && (
        <Card className="rounded-3xl p-6 border-emerald-300 bg-emerald-50 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto"><Check size={32} strokeWidth={3} /></div>
          <h2 className="font-extrabold text-lg mt-3 text-emerald-800">ଧନ୍ୟବାଦ!</h2>
          <p className="text-sm text-emerald-700 mt-1">ଆପଣଙ୍କ ପେମେଣ୍ଟ ଆବେଦନ ଯାଞ୍ଚ ପାଇଁ ଆସିଛି। ୨୪ ଘଣ୍ଟା ମଧ୍ୟରେ Premium activate ହେବ।</p>
          <p className="text-[11px] text-emerald-600 mt-3">Transaction: {txnId}</p>
          <Link to="/" className="inline-block mt-4 bg-emerald-600 text-white font-bold px-5 py-2 rounded-full text-sm">Home ଯାଆନ୍ତୁ</Link>
        </Card>
      )}
    </div>
  );
}
